import React from "react";
import { formatProposalInvestment } from "../lib/proposalUploadMock";

const BG = "#FCFCFC";
const TEXT = "#222222";
const ACCENT = "#001EFF";
const RULE = "#E8E4DC";
const META = "#888888";
const FONT = "'Nohemi', 'Space Grotesk', sans-serif";

function formatMoney(value) {
  const amount = Number(value || 0);
  if (Number.isInteger(amount)) return `$${amount.toLocaleString()}`;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** In-app preview line: Founder • Company (PDF still uses company alone). */
function formatScopeClientLine(payload) {
  const company = String(payload?.client_name || "").trim();
  const founder = String(
    payload?.founder_name || payload?.primary_contact_name || payload?.contact_name || "",
  ).trim();
  if (founder && company) return `${founder} • ${company}`;
  return founder || company || "";
}

const RHYTHM_ITEMS = [
  "A shared client portal to track phases, milestones, and progress in real time.",
  "Consistent touchpoints to stay aligned on direction, priorities, and momentum.",
  "Regular updates shared on progress, decisions, and what comes next.",
];

const BARTER_RHYTHM_ITEMS = [
  "Each cycle, a fresh scope of what we’re working on together.",
  "A shared client portal to track phases, milestones, and progress in real time.",
  "No invoice on this exchange — the agreement is the lock-in; scopes carry the work forward.",
];

export function ScopeSection({
  payload,
  investmentAtBottom = false,
  sectionTitle = "Scope of Support",
  addonSelected = false,
  onAddonChange,
  canSelectAddon = false,
  showAddonControls = false,
}) {
  const raw = Array.isArray(payload.scope_items) ? payload.scope_items : [];
  const items = raw
    .filter((it) => ((it?.title || "").trim() || (it?.description || "").trim()))
    .map((it, idx) => ({ ...it, displayNumber: String(idx + 1).padStart(2, "0") }));
  const exchange = String(payload.exchange || payload.they_provide || "").trim();
  const terms = String(payload.terms || "").trim();
  const isBarter =
    payload.compensation_type === "barter" ||
    payload.scope_only === true ||
    Boolean(exchange) ||
    Boolean(terms);
  const investmentLine = !isBarter ? (
    <p style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, marginTop: investmentAtBottom ? 22 : 22, marginBottom: investmentAtBottom ? 16 : 12, color: ACCENT }}>
      <strong style={{ fontWeight: 500 }}>INVESTMENT:</strong>{" "}
      {formatProposalInvestment(
        payload.investment_total,
        payload.timeline || payload.date_range,
      )}
    </p>
  ) : null;
  const exchangeBlock = isBarter && exchange ? (
    <div style={{ marginTop: investmentAtBottom ? 22 : 22, marginBottom: 16 }} data-testid="scope-review-exchange">
      <p style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: ACCENT, margin: "0 0 8px" }}>
        THEY PROVIDE
        <span style={{ fontWeight: 300 }}> · in place of invoice</span>
      </p>
      <p
        style={{
          fontFamily: FONT,
          fontWeight: 300,
          fontSize: 12,
          lineHeight: 1.65,
          color: TEXT,
          margin: 0,
          whiteSpace: "pre-wrap",
        }}
      >
        {exchange}
      </p>
    </div>
  ) : null;
  const termsBlock = isBarter && terms ? (
    <div style={{ marginBottom: 16 }} data-testid="scope-review-terms">
      <p style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: ACCENT, margin: "0 0 8px" }}>
        TERMS
      </p>
      <p
        style={{
          fontFamily: FONT,
          fontWeight: 300,
          fontSize: 12,
          lineHeight: 1.65,
          color: TEXT,
          margin: 0,
          whiteSpace: "pre-wrap",
        }}
      >
        {terms}
      </p>
    </div>
  ) : null;
  // Review card eyebrow: scope packets are always one option → "Scope of Support".
  // Named proposal options (e.g. Notion OS) keep their label.
  const reviewCard = Boolean(investmentAtBottom);
  const rawLabel = (payload.option_label || "").trim();
  const genericOption = /^option\s*\d+\.?$/i.test(rawLabel);
  const eyebrow =
    reviewCard && (sectionTitle === "Scope of Support" || !rawLabel || genericOption)
      ? sectionTitle
      : reviewCard
        ? rawLabel
        : sectionTitle;
  const timeline = (payload.timeline || payload.date_range || "").trim();
  const addon = payload?.addon && typeof payload.addon === "object" ? payload.addon : null;
  const showAddon = Boolean(
    !isBarter && showAddonControls && addon && (addon.title || addon.description || addon.amount > 0),
  );
  const addonTitle = String(addon?.title || "Add-on")
    .trim()
    .replace(/^add[-\s]?on:\s*/i, "");

  const addonBlock = showAddon ? (
    <div style={{ marginTop: 18 }} data-testid="scope-review-addon">
      <hr style={{ border: "none", borderTop: `0.5px solid ${RULE}`, margin: "0 0 16px" }} />
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          cursor: canSelectAddon ? "pointer" : "default",
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(addonSelected)}
          disabled={!canSelectAddon}
          onChange={(e) => onAddonChange?.(e.target.checked)}
          data-testid="scope-review-addon-check"
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span
            style={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: 12,
              color: ACCENT,
              marginBottom: 6,
              display: "block",
            }}
          >
            Add-on: {addonTitle || "Add-on"}
            {Number(addon.amount) > 0 ? (
              <span style={{ fontWeight: 300 }}> · {formatMoney(addon.amount)}</span>
            ) : null}
          </span>
          {addon.description ? (
            <span
              style={{
                fontFamily: FONT,
                fontWeight: 300,
                fontSize: 12,
                lineHeight: 1.65,
                color: TEXT,
                margin: 0,
                whiteSpace: "pre-wrap",
                display: "block",
              }}
            >
              {addon.description}
            </span>
          ) : null}
        </span>
      </label>
    </div>
  ) : null;

  return (
    <section style={{ marginBottom: investmentAtBottom ? 0 : 28 }}>
      <h2
        style={{
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: 12,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: ACCENT,
          margin: "0 0 16px",
        }}
      >
        {eyebrow}
      </h2>
      <div style={{ fontSize: 12, color: TEXT, opacity: 0.85, marginBottom: 8 }}>
        {formatScopeClientLine(payload)}
      </div>
      {!reviewCard && rawLabel && !genericOption ? (
        <div style={{ fontSize: 11, color: ACCENT, opacity: 0.9, marginBottom: 8, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {rawLabel}
        </div>
      ) : null}
      {timeline ? (
        <div style={{ fontSize: 12, color: TEXT, opacity: 0.75, marginBottom: 20 }}>
          {reviewCard ? `Timeline: ${timeline}` : timeline}
        </div>
      ) : null}
      {payload.intro ? (
        <p style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, lineHeight: 1.7, color: TEXT, margin: "0 0 20px" }}>{payload.intro}</p>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {items.map((it, idx) => (
          <div key={idx}>
            <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: ACCENT, marginBottom: 6 }}>
              {it.displayNumber ? `${it.displayNumber} — ` : ""}
              {it.title || "Scope item"}
            </div>
            {it.description ? (
              <p style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, lineHeight: 1.65, color: TEXT, margin: 0, whiteSpace: "pre-wrap" }}>
                {it.description}
              </p>
            ) : null}
          </div>
        ))}
      </div>
      {!investmentAtBottom ? investmentLine : null}
      {!investmentAtBottom ? exchangeBlock : null}
      {!investmentAtBottom ? termsBlock : null}
      <p style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, fontStyle: "italic", lineHeight: 1.6, color: TEXT, opacity: 0.9, margin: investmentAtBottom ? "22px 0 16px" : "0 0 16px" }}>
        What&apos;s needed may evolve as we go. Priorities are set with intention and held with flexibility.
      </p>
      <div style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, lineHeight: 1.65, color: TEXT, marginBottom: 12 }}>
        <strong style={{ fontWeight: 500 }}>Rhythm of work</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: 0, listStyle: "none" }}>
          {(isBarter ? BARTER_RHYTHM_ITEMS : RHYTHM_ITEMS).map((line) => (
            <li key={line} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <span style={{ color: ACCENT, flexShrink: 0 }}>+</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
      {investmentAtBottom ? investmentLine : null}
      {investmentAtBottom ? exchangeBlock : null}
      {investmentAtBottom ? termsBlock : null}
      {addonBlock}
    </section>
  );
}

export function InvoiceSection({ payload, locked = false }) {
  const credits = Number(payload.credits || 0);
  const showCredit = credits > 0;
  const invoiceNumber = String(payload.invoice_number || "").trim() || "INV-XXX";
  const isPaid = Boolean(
    payload.already_paid ||
      payload.paid ||
      String(payload.invoice_status || "").toUpperCase() === "PAID",
  );
  const paidAt = String(payload.paid_at || "").trim();
  return (
    <section style={{ position: "relative" }} data-testid={locked ? "invoice-section-locked" : "invoice-section"}>
      {locked ? (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-[rgba(252,252,252,0.92)] px-6 text-center"
          data-testid="invoice-lock-overlay"
        >
          <p
            style={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACCENT,
              margin: 0,
            }}
          >
            Invoice locked
          </p>
          <p style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, lineHeight: 1.6, color: META, margin: 0 }}>
            {isPaid
              ? "Accept this scope to view your invoice."
              : "Accept this scope to unlock your invoice."}
          </p>
        </div>
      ) : null}
      <h2
        style={{
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: 12,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: ACCENT,
          margin: "0 0 18px",
        }}
      >
        Invoice
      </h2>
      <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: TEXT, marginBottom: 4 }}>TAĪSTU</div>
      <div style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, color: TEXT, opacity: 0.85, lineHeight: 1.5, marginBottom: 16 }}>
        tai@taistu.com
        <br />
        taistu.com
      </div>
      <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: ACCENT, marginBottom: 12 }}>
        {locked ? "INV-···" : invoiceNumber}
      </div>
      <div style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, color: TEXT, lineHeight: 1.6, marginBottom: 16 }}>
        Issued: {payload.issued_date || "—"}
        <br />
        Due: {payload.due_date || "—"}
        <br />
        Bill to: {payload.client_name}
        {isPaid && !locked ? (
          <>
            <br />
            Paid{paidAt ? `: ${paidAt}` : ""}
          </>
        ) : null}
      </div>
      <div style={{ borderTop: `0.5px solid ${RULE}`, paddingTop: 14, marginBottom: 12 }}>
        <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: TEXT, marginBottom: 6 }}>{payload.invoice_service_title || "Services"}</div>
        {payload.invoice_service_description ? (
          <p style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, lineHeight: 1.65, color: TEXT, margin: "0 0 12px", whiteSpace: "pre-wrap" }}>
            {payload.invoice_service_description}
          </p>
        ) : null}
      </div>
      <div style={{ fontFamily: FONT, fontWeight: 300, fontSize: 12, color: TEXT, lineHeight: 1.85 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
          <span>Subtotal</span>
          <span>{formatMoney(payload.subtotal)}</span>
        </div>
        {showCredit ? (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
            <span>{payload.credit_label || "Credit"}</span>
            <span>-{formatMoney(credits)}</span>
          </div>
        ) : null}
        {isPaid && !locked ? (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
            <span>Paid</span>
            <span>-{formatMoney(payload.total)}</span>
          </div>
        ) : null}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontWeight: 500, marginTop: 6, paddingTop: 8, borderTop: `0.5px solid ${RULE}` }}>
          <span>{isPaid && !locked ? "Balance due" : "Total"}</span>
          <span>
            {formatMoney(isPaid && !locked ? 0 : payload.total)}
            {isPaid && !locked ? (
              <span style={{ color: ACCENT, fontWeight: 500, marginLeft: 6 }}>Paid</span>
            ) : null}
          </span>
        </div>
      </div>
      <p
        style={{
          fontFamily: FONT,
          fontWeight: 300,
          fontSize: 12,
          lineHeight: 1.6,
          color: TEXT,
          marginTop: 18,
          marginBottom: 0,
        }}
      >
        Sent w/ the most gratitude for you. {"<"}3
      </p>
    </section>
  );
}

export function BLUPreview({
  payload,
  busy,
  onFinalize,
  interactive = true,
  /** Scope-only view for prospect review links (no invoice page). */
  scopeOnly = false,
  /** When invoice is shown, dim it until the prospect accepts. */
  invoiceLocked = false,
  packetFileId = null,
  packetInvoiceNumber = null,
  packetActions = null,
  sectionTitle = "Scope of Support",
  addonSelected = false,
  onAddonChange,
  canSelectAddon = false,
  showAddonControls = false,
}) {
  if (!payload) return null;
  const clientName = (payload.client_name || "").trim() || "client";
  const optionLabel = (payload.option_label || "").trim();
  const packetReady = Boolean(packetFileId);
  const createLabel = optionLabel
    ? `Create packet · ${optionLabel}`
    : `Create packet for ${clientName}`;

  return (
    <div
      className="blu-document-preview"
      style={{
        width: "100%",
        maxWidth: interactive ? 440 : "100%",
        boxSizing: "border-box",
        margin: interactive ? undefined : "0 auto",
        background: BG,
        color: TEXT,
        border: `0.5px solid ${RULE}`,
        borderRadius: 12,
        padding: "22px 22px 20px",
        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
      }}
      data-testid="blu-preview"
    >
      <ScopeSection
        payload={payload}
        investmentAtBottom
        sectionTitle={sectionTitle}
        addonSelected={addonSelected}
        onAddonChange={onAddonChange}
        canSelectAddon={canSelectAddon}
        showAddonControls={showAddonControls}
      />
      {!scopeOnly ? (
        <>
          <hr style={{ border: "none", borderTop: `0.5px solid ${RULE}`, margin: "8px 0 24px" }} />
          <InvoiceSection payload={payload} locked={invoiceLocked} />
        </>
      ) : null}
      {interactive && !packetReady ? (
        <button
          type="button"
          className="t-btn-primary mt-5 min-h-[44px] w-full !rounded-[var(--t-event-pill-radius)] text-[12px] font-light"
          style={{ background: ACCENT, borderColor: ACCENT }}
          onClick={onFinalize}
          disabled={busy}
          data-testid="blu-finalize-btn"
        >
          {busy ? "Creating packet…" : createLabel}
        </button>
      ) : null}
      {interactive && packetReady ? (
        <div className="mt-5 space-y-2" data-testid="blu-packet-ready">
          <p
            style={{
              fontFamily: FONT,
              fontWeight: 300,
              fontSize: 12,
              color: META,
              margin: 0,
            }}
          >
            Packet ready{packetInvoiceNumber ? ` (${packetInvoiceNumber})` : ""}. Email or download to send.
          </p>
          {packetActions}
        </div>
      ) : null}
    </div>
  );
}
